import { IndexedObject } from 'services/indexedObject';

// Mirrors pm.api pm/assessment (views.py + serializers.py). All endpoints live
// under /assessment_sessions/:session_id/ and use the normal auth flow.

export type AssessmentInstrumentKind = 'rates' | 'cash' | 'option';

export type AssessmentInstrument = {
  code: string;
  kind: AssessmentInstrumentKind;
  parent: string | null;
  // These are separate on purpose. A scenario may let the player record an
  // informational conviction for a tradable instrument while explicitly
  // excluding that instrument from every assessment pillar.
  assessment_scoring?: 'scored' | 'ignored';
  conviction_allowed?: boolean;
  // Why an instrument the class can trade is NOT graded, when the answer key
  // left it out on purpose (an option graded through its parent, a currency
  // pair PM added at start so trainees can exchange currency, a code the
  // matrix has no column for). `not_graded_reason` is the stable code;
  // `not_graded_sentence` is the sentence a trainee is shown. Absent on a
  // graded row and on a payload from before the answer key carried them.
  not_graded_reason?: string;
  not_graded_sentence?: string;
  // Definition-time FK. Goes stale across blueprint regenerations and is only
  // ever ONE id — display/back-compat, never join on it.
  underlying_asset: number | null;
  // Per-session resolved asset ids (one code can span several assets, e.g.
  // sequential bond issues). This is the join key for session asset rows.
  // Optional only until every environment serves the new state payload.
  underlying_assets?: number[];
};

export type AssessmentAssignment = {
  id: number;
  news: number;
  instrument_code: string;
  period: number;
  created_at: string;
  revoked_at: string | null;
  // Client-only: true while an optimistic assignment awaits its 201.
  pending?: boolean;
};

export type AssessmentSliderEvent = {
  id: number;
  instrument_code: string;
  period: number;
  value: number;
  created_at: string;
};

// 'pending' | 'running' | 'done' | 'failed' (link.get_status_display()).
export type AssessmentLinkStatus = string;

export type AssessmentStateResponse = {
  assessment?: null;
  capture_only?: boolean;
  scenario?: string;
  status?: AssessmentLinkStatus;
  current_period?: number | null;
  /** AssessmentPeriod database id; event.period uses this value, not the index. */
  current_period_id?: number | null;
  instruments?: AssessmentInstrument[];
  assignments?: AssessmentAssignment[];
  sliders?: AssessmentSliderEvent[];
};

export type AssessmentRiskInstrument = {
  code: string;
  position: number;
  unit: string;
  soft_limit: number;
  utilization: number;
  breached: boolean;
  dv01_k_bp?: number;
  dv01_limit?: number;
  dv01_utilization?: number;
  dv01_breached?: boolean;
};

export type AssessmentPortfolioCheck = {
  name: string;
  value: number;
  limit: number;
  utilization: number;
  breached: boolean;
};

export type AssessmentDrawdown = {
  nav_m: number;
  drawdown_m: number;
  stop_m: number;
  stopped: boolean;
};

export type AssessmentRiskPanel =
  | {
      capture_only?: false;
      period: number | null;
      instruments: AssessmentRiskInstrument[];
      portfolio: AssessmentPortfolioCheck[];
      drawdown: AssessmentDrawdown;
    }
  | { capture_only: true; instruments: []; portfolio: null };

export type AssessmentResultRow = {
  id: number;
  player: number;
  player_name: string;
  model_version: string;
  pillar_1: number;
  pillar_2: number;
  pillar_3: number;
  pillar_4_provisional: number;
  pillar_5_provisional: number;
  official_composite: number;
  illustrative_composite: number;
  rating: string;
  rank: number;
  /**
   * What this candidate actually did in the room: `traded` (at least one order
   * or currency exchange), `voted_only` (conviction sliders and/or news
   * assignments, no trade) or `absent` (nothing).
   *
   * It travels with the rating because the score no longer implies a trade:
   * pillar 1 is a third of the official composite and is earned entirely on
   * the conviction and news surfaces. Rendered by `ParticipationNote`
   * (components/Participation) wherever a score is rendered.
   *
   * Optional only for a response captured before the field existed; the marker
   * then renders nothing rather than guessing.
   */
  participation?: string | null;
  /**
   * Share (0..1) of the conviction-slider surface the candidate set a view
   * on. Rendered by `ConvictionCoverageNote` beside the participation flag
   * when it is under half; null when not on record.
   */
  conviction_coverage?: number | null;
  computed_at: string;
};

export type AssessmentResultsResponse = {
  capture_only?: boolean;
  status: AssessmentLinkStatus;
  error: string | null;
  computed_at: string | null;
  results: AssessmentResultRow[];
  /**
   * The validity verdict of the answer key these scores were graded against,
   * stamped onto the session link when it was attached
   * (`link_validity_snapshot`, pm/assessment/services/attach_gate.py). Read
   * with `readAnswerKeyGate` (components/VerdictBanner/gate.ts) — typed
   * loosely on purpose so one reader owns the shape and no screen
   * destructures it by hand.
   *
   * Absent means the endpoint did not serve it; the banner then renders
   * nothing rather than claiming no check exists.
   */
  validity?: unknown;
  /** The same document under its product name, read as an alias. */
  answer_key_gate?: unknown;
};

// GET result_detail/?player=<id> — per-player pillar breakdown backing the
// trainer report tabs. Pillars 1/2/3/5 are period-by-period component tables;
// pillar 4 (Updating) is transition-based and shaped differently.

export type AssessmentPillarPeriod = {
  period: number;
  label: string;
  regime: string;
  score: number;
  components: { [component: string]: number };
};

export type AssessmentPillar4Components = {
  slider_revision_fit: number;
  position_revision_fit: number;
  invalidation_response: number;
  no_action_proxy: number;
  pillar_4_provisional: number;
  evidence_coverage: number;
  status: string;
};

export type AssessmentPillar4Transition = {
  from_period: number;
  to_period: number;
  instrument_parent: string;
  event_type: string;
  slider_revision_fit: number | null;
  position_revision_fit: number | null;
  invalidation_response: number | null;
  no_action_proxy: number | null;
};

// best/weakest arrive as whatever the scorer emits (a transition-shaped
// object or a plain label) — the view renders these defensively.
export type AssessmentPillar4TransitionRef =
  | (Partial<AssessmentPillar4Transition> & { score?: number })
  | string
  | null;

export type AssessmentPillar4Diagnostics = {
  best_transition?: AssessmentPillar4TransitionRef;
  weakest_transition?: AssessmentPillar4TransitionRef;
  hard_events: number;
  strong_hard_responses: number;
  decay_events: number;
  strong_decay_responses: number;
  no_action_events: number;
  strong_no_action_responses: number;
};

export type AssessmentPillar4Detail = {
  components: AssessmentPillar4Components;
  transitions: AssessmentPillar4Transition[];
  diagnostics: AssessmentPillar4Diagnostics;
  evidence_coverage: number;
};

export type AssessmentResultDetailSummary = {
  rank: number;
  pillar_1: number;
  pillar_2: number;
  pillar_3: number;
  pillar_4_provisional: number;
  pillar_5_provisional: number;
  official_composite: number;
  illustrative_composite: number;
  rating: string;
  total_pnl_m: number;
  ending_nav_m: number;
  computed_at: string;
};

export type AssessmentPillarPeriods = { periods: AssessmentPillarPeriod[] };

export type AssessmentResultDetail = {
  player: number;
  player_name: string;
  model_version: string;
  summary: AssessmentResultDetailSummary;
  pillars: {
    '1'?: AssessmentPillarPeriods;
    '2'?: AssessmentPillarPeriods;
    '3'?: AssessmentPillarPeriods;
    '4'?: AssessmentPillar4Detail;
    '5'?: AssessmentPillarPeriods;
  };
};

export type AssessmentScenarioOption = {
  code: string;
  name: string;
  model_version: string;
  status: string; // 'draft' | 'active'
  periods: number;
};

// GET debug/?player=<id> (admin only) - QA/inspection payload behind the
// trainer report's Debug tab. Without ?player the response is just the
// envelope (scenario/link/resolver map/players); with a player it also
// carries the per-period ledger and totals.

export type AssessmentDebugPosition = {
  code: string;
  position: number;
  unit: string;
  premium_m?: number | null;
  asset_ids?: number[];
};

export type AssessmentDebugOptimalLine = {
  code: string;
  target: number;
  util?: number | null;
};

export type AssessmentDebugSummary = {
  gross_cash?: number | null;
  bond_gross?: number | null;
  net_dv01?: number | null;
  gross_dv01?: number | null;
  equity_gross?: number | null;
  equity_net?: number | null;
  fx_gross?: number | null;
  common_usd?: number | null;
  oil_gross?: number | null;
  option_premium?: number | null;
  stress_loss?: number | null;
  // Present only when the session derives a short pool (see session_limits).
  short_book?: number | null;
  weighted_excess?: number | null;
};

// Pillar-1 evidence trail: what the benchmark expected per headline vs what
// the candidate actually linked. |ICS| >= 0.25 is the relevance threshold the
// engine's score_mapping_for_period uses.
export type AssessmentDebugVerdict = {
  code: string;
  name: string; // PM asset name + ticker
  ics: number | null;
  material: boolean; // |ICS| >= 0.25
  linked: boolean;
  verdict:
    | 'true positive'
    | 'false positive'
    | 'false negative'
    | 'true negative';
  explain: string;
};

export type AssessmentDebugNews = {
  news_code: string;
  headline: string;
  timestamp: string | null;
  theme: string;
  clarity: number | null;
  ics: { [code: string]: number | null };
  relevant: string[];
  assigned: string[];
  hits: string[];
  false: string[];
  missed: string[];
  verdicts: AssessmentDebugVerdict[];
};

export type AssessmentDebugConviction = {
  code: string;
  name: string;
  explain: string;
  slider: number | null;
  pcs: number | null;
  agreement: number | null;
  direction_ok: boolean | null;
  gap: number | null;
};

export type AssessmentDebugSignal = {
  news: AssessmentDebugNews[];
  conviction: AssessmentDebugConviction[];
  mapping_counts: {
    tp: number;
    fp: number;
    fn: number;
    precision: number;
    recall: number;
    tn: number;
    f1_unweighted: number;
    assignments: number;
  };
};

export type AssessmentDebugMetric = {
  key: string;
  label: string;
  meaning: string;
  tooltip: string; // plain-English hover text
  weight: number | null; // null = diagnostic, not scored
  value: number;
  contribution: number | null; // points this added to the 0-100 pillar score
};

export type AssessmentDebugExplainedPillar = {
  title: string;
  score: number | null;
  official: boolean; // false for the provisional P4/P5
  metrics: AssessmentDebugMetric[];
};

export type AssessmentDebugPillarPeriod = {
  score: number | null;
  components: { [key: string]: unknown };
};

export type AssessmentDebugScoring = {
  model_version: string;
  computed_at: string | null;
  pillars: {
    '1': number | null;
    '2': number | null;
    '3': number | null;
    '4_provisional': number | null;
    '5_provisional': number | null;
  };
  official_composite: number | null;
  official_composite_check: number | null;
  official_formula: string;
  illustrative_composite: number | null;
  rating: string;
  rank: number | null;
  // 'traded' | 'voted_only' | 'absent' — what the candidate did in the room.
  // Optional because a payload built before the flag existed carries none.
  participation?: string | null;
  // Share (0..1) of the conviction-slider surface set; see ConvictionCoverageNote.
  conviction_coverage?: number | null;
  note?: string | null;
  evidence_coverage?: { [key: string]: number } | null;
  p4_components?: { [key: string]: unknown } | null;
  p5_components?: { [key: string]: unknown } | null;
  p4_diagnostics?: { [key: string]: unknown } | null;
  // period index -> pillar ('1'|'2'|'3'|'5') -> score + components
  per_period: {
    [periodIndex: string]: { [pillar: string]: AssessmentDebugPillarPeriod };
  };
  explained: {
    [periodIndex: string]: { [pillar: string]: AssessmentDebugExplainedPillar };
  };
  pillar_titles: { [pillar: string]: string };
};

// Which limits produced the scores, beside the session's own pre-trade caps.
export type AssessmentDebugRiskFramework = {
  instrument: {
    code: string;
    scoring_soft_limit_m: number | null; // what scoring actually used
    stored_soft_limit_m?: number | null; // the row's value, for reference
    pm_pretrade_cap_m: number | null;
    diverges: boolean;
    // The working. PM sets the percentage per ASSET CLASS; the pre-trade check
    // applies it to each instrument separately.
    asset_class?: string | null;
    class_limit_pct?: number | null;
    margin_pct?: number | null;
    asset_count?: number | null;
    per_asset_m?: number | null;
    formula?: string | null;
  }[];
  portfolio_scoring: { [key: string]: number };
  // Same checks as rows the UI can table, each saying where its number came
  // from and what it means in plain language.
  portfolio_rows?: {
    key: string;
    label: string;
    unit: string;
    meaning: string;
    limit: number | null;
    source: 'session' | 'reference';
    derivation: string | null;
  }[];
  session_settings?: {
    label: string;
    value: number | boolean | null;
    unit: string;
    note: string;
  }[];
  pooled_caps_m?: { [key: string]: number };
  // What the framework still cannot derive from PM, stated openly in the UI.
  known_limitations?: {
    key: string;
    title: string;
    what: string;
    why: string;
    impact: string;
    plan: string;
  }[];
  portfolio_session: {
    fx_book_m: number | null;
    short_book_m: number | null;
    hard_limits: boolean;
    bond_leverage: number;
    equity_leverage: number;
    fx_leverage: number;
    short_sell_leverage: number;
    note: string;
  };
};

// One optimal-book line, fully explained: conviction -> band -> agreement
// discount -> final size, its use of its OWN cap (final_util, does NOT sum
// across lines) and its share of the stress budget (stress_share, DOES sum).
export type AssessmentDebugOptimalDetailLine = {
  code: string;
  pcs: number | null;
  agreement: number | null;
  band_util: number | null;
  conviction_util: number | null;
  final_util: number | null;
  cap_m: number | null;
  target: number | null;
  unit: string | null;
  premium_m: number | null;
  premium_cap_m: number | null;
  stress_m: number | null;
  stress_share: number | null;
  stress_shock?: number | null;
  shock_unit?: string | null;
  dv01_per_1m?: number | null;
  why: string;
  // The arithmetic with this line's real numbers substituted in, so a reader
  // can redo the sum by hand.
  steps?: { n: number; title: string; detail: string }[];
};

export type AssessmentDebugOptimalBudget = {
  key: string;
  label: string;
  used: number | null;
  limit: number | null;
  pct: number | null;
  source: 'session' | 'reference';
  // How the "used" figure is computed, in words.
  equation?: string | null;
};

export type AssessmentDebugOptimalDetail = {
  lines: AssessmentDebugOptimalDetailLine[];
  budgets: AssessmentDebugOptimalBudget[];
  stress_budget_m: number | null;
  stress_total_m: number | null;
  stress_total_share: number | null;
  targets_source: string;
  tooltips: { [key: string]: string };
  // "Shape of the book" ratios, in words rather than a raw aggregates dump.
  shape?: {
    key: string;
    label: string;
    value: number | null;
    meaning: string;
    equation?: string | null;
  }[];
  // Codes whose stored target was baked under limits no longer in force.
  stale_targets?: string[];
  aggregates?: { [key: string]: number };
};

export type AssessmentDebugPeriod = {
  index: number;
  label: string;
  start_date: string;
  end_date: string;
  regime: string;
  positions: AssessmentDebugPosition[];
  summary: AssessmentDebugSummary;
  optimal: AssessmentDebugOptimalLine[];
  optimal_detail?: AssessmentDebugOptimalDetail | null;
  // Raw engine benchmark book for the period - rendered verbatim.
  benchmark_portfolio: { [key: string]: unknown };
  pcs: { [code: string]: number };
  agreement: { [code: string]: number | null };
  signal: AssessmentDebugSignal | null;
  nav_pm: number | null;
  nav_delta: number | null;
  pnl_model: number | null;
};

export type AssessmentDebugPlayer = {
  id: number;
  username: string;
  utilized: boolean;
};

export type AssessmentDebugResponse = {
  scenario: string;
  model_version: string;
  link_status: AssessmentLinkStatus;
  link_error: string | null;
  instrument_map: { [code: string]: number[] };
  map_problems: string[];
  players: AssessmentDebugPlayer[];
  player?: number | null;
  periods?: AssessmentDebugPeriod[];
  totals?: { total_pnl_m: number; ending_nav_m: number };
  display_names?: { [code: string]: string };
  scoring?: AssessmentDebugScoring | null;
  risk_framework?: AssessmentDebugRiskFramework;
};

// GET validation/?scenario=<code> (admin only) - model sanity diagnostics
// from pm.api services/validation_score.py's validation_report().

export type AssessmentValidationInstrument = {
  instrument: string;
  scale_75pct_abs_move: number | null;
  correlation: number | null;
  directional_hit_rate: number | null;
  hits: number;
  eligible: number;
  periods_with_move: number;
};

export type AssessmentValidationPeriodRow = {
  period: number;
  label: string;
  instrument: string;
  pcs: number | null;
  realized_move: number | null;
  move_source: string | null;
  realized_score: number | null;
  eligible: boolean;
  hit: boolean | null;
};

export type AssessmentValidationResponse = {
  scenario: string;
  model_version: string;
  neutral_band: number;
  instruments: AssessmentValidationInstrument[];
  periods: AssessmentValidationPeriodRow[];
  summary: {
    min_correlation: number | null;
    max_correlation: number | null;
    min_hit_rate: number | null;
    max_hit_rate: number | null;
    instruments_reported: number;
    note: string;
  };
};

// POST recompute_optimal/ (admin only) - the Debug tab's Operations card.
// Rebuilds this session's optimal book under its own limits (D6). The
// re-match-news and bind-blueprint operations are retired: the answer key is
// derived from the started-session shape and bound to its family by identity
// hash, so neither is an admin action any more.

export type AssessmentRecomputeOptimalResponse = {
  scenario: string;
  periods: number;
  targets_written: number;
  notes?: string[];
};

export type AssessmentOperationKind = 'recompute_optimal';

export type AssessmentOperationResult = {
  // Increments on every completed request so the Debug tab can react to a
  // completion exactly once (refetch the debug envelope + validation).
  seq: number;
  kind: AssessmentOperationKind;
  ok: boolean;
  message: string;
  description?: string;
};

export type AssessmentState = {
  // Browser-only ordering guards; never persisted as assessment evidence.
  capture_sync?: import('./captureRequests').CaptureSync;
  capture_scope?: string;
  // A snapshot failure belongs only to this session/player/period. Never store
  // server diagnostics here: participants receive a neutral retry message.
  capture_error_scope?: string;
  current_period_id?: number | null;
  // GET state/ — per-session, per-player snapshot.
  attached: boolean; // false once state/ answered {assessment: null}
  capture_only?: boolean;
  fetched: boolean;
  fetching: boolean;
  scenario?: string;
  status?: AssessmentLinkStatus;
  current_period?: number | null;
  instruments: AssessmentInstrument[];
  // Active (non-revoked) assignments for the requesting player, by id.
  assignments: IndexedObject<AssessmentAssignment>;
  // Latest conviction per instrument code for the CURRENT period only (server
  // keeps the full event log; scoring treats each period independently, so a
  // code absent here means "not set this period" — scored neutral 0).
  sliders: { [instrumentCode: string]: number };
  // GET risk/
  risk: {
    item?: AssessmentRiskPanel;
    fetching: boolean;
    fetched: boolean;
  };
  // GET result_detail/?player= (trainer/admin only) — cached by player id.
  // `fetching` keys track requests: true while in flight, false once a
  // request failed (so effects don't re-dispatch in a loop; failed markers
  // are cleared on the next results fetch, i.e. the Refresh button retries).
  resultDetails: {
    items: { [player: number]: AssessmentResultDetail };
    fetching: { [player: number]: boolean };
  };
  // GET results/ (trainer/admin only)
  results: {
    capture_only?: boolean;
    status?: AssessmentLinkStatus;
    error?: string | null;
    computed_at?: string | null;
    items: AssessmentResultRow[];
    /** Whatever the endpoint served about the answer key's validity gate. */
    answerKeyGate?: unknown;
    fetching: boolean;
    fetched: boolean;
    recomputing: boolean;
  };
  // GET scenarios/ + POST attach/ (trainer/admin attach UI)
  scenarios: {
    items: AssessmentScenarioOption[];
    fetching: boolean;
    fetched: boolean;
  };
  attaching: boolean;
  // GET debug/?player= (admin only) - envelope cached once, per-player
  // payloads cached by id with resultDetails-style fetch markers (true = in
  // flight, false = attempted & failed - effects must not auto-retry; the
  // tab's Refresh button re-dispatches unconditionally).
  debug: {
    envelope?: AssessmentDebugResponse;
    // undefined = never requested; true = in flight; false = completed.
    envelopeFetching?: boolean;
    items: { [player: number]: AssessmentDebugResponse };
    fetching: { [player: number]: boolean };
  };
  // GET validation/?scenario= (admin only) - one scenario cached at a time.
  // `fetching` follows the same tri-state as debug.envelopeFetching.
  validation: {
    item?: AssessmentValidationResponse;
    scenario?: string;
    fetching?: boolean;
  };
  // Admin operations card (Debug tab). Results are surfaced inline in the
  // card, never via the global toast (assessment actions are exempt - see
  // views/NotificationCenter/Operations.tsx).
  operations: {
    recomputingOptimal: boolean;
    lastResult?: AssessmentOperationResult;
  };
};

export const AssessmentInitialState: AssessmentState = {
  attached: false,
  fetched: false,
  fetching: false,
  instruments: [],
  assignments: {},
  sliders: {},
  risk: { fetching: false, fetched: false },
  resultDetails: { items: {}, fetching: {} },
  results: { items: [], fetching: false, fetched: false, recomputing: false },
  scenarios: { items: [], fetching: false, fetched: false },
  attaching: false,
  debug: { items: {}, fetching: {} },
  validation: {},
  operations: {
    recomputingOptimal: false,
  },
};
